import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import Button from "components/Button";
import SearchInput from "components/SearchInput";

import InvoicesTable from "../Table";
import InvoiceFilters from "./InvoiceFilters";

import { FilterValue } from "components/TableFilters/types";
import { InvoiceFiltersState } from "./InvoiceFilters/types";

import { IbanInvoiceGetByFilterQuery } from "api";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { searchIbanInvoices } from "store/features/ibanInvoiceSlice";

import { routes } from "routes/routes";

import rowsPerPageService, { RowsPerPageKeys } from "services/rowsPerPage";
import { formatInvoiceFilters } from "shared/functions/formatInvoiceFilters";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

const InvoicesMain: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [isInvoicesLoading, setIsInvoicesLoading] = useState(false);

  const [activeFilters, setActiveFilters] = useState<InvoiceFiltersState>({
    favorite: false,
    ibans: [],
    identificationCodes: [],
    invoiceDateRange: { id: "all_time", range: null },
    expirationDateRange: { id: "all_time", range: null },
  });

  const [searchText, setSearchText] = useState<string>("");

  const globalLoading = useAppSelector((state) => state.loading.isLoading);
  const ibanInvoices = useAppSelector(
    (state) => state.ibanInvoice.ibanInvoices,
  );

  const pageSize = useAppSelector((state) => state.ibanInvoice.pageSize);
  const pageCount = useAppSelector((state) => state.ibanInvoice.pageCount);
  const pageNumber = useAppSelector((state) => state.ibanInvoice.pageNumber);

  useEffect(() => {
    const apiFilters = formatInvoiceFilters(activeFilters);

    getIbanInvoices({
      ...apiFilters,
      searchText,
      pageNumber: 1,
      pageSize,
    });
  }, [
    activeFilters.favorite,
    activeFilters.ibans,
    activeFilters.identificationCodes,
    activeFilters.invoiceDateRange.range,
    activeFilters.expirationDateRange.range,
    activeFilters.expirationDateRange.noExpiration,
  ]);

  const onPageChange = useCallback(
    (page: number) => {
      const apiFilters = formatInvoiceFilters(activeFilters);

      getIbanInvoices({
        ...apiFilters,
        searchText,
        pageNumber: page,
        pageSize,
      });
    },
    [pageSize, searchText, activeFilters],
  );

  const onPageSizeChange = useCallback(
    (pageSize: number) => {
      const apiFilters = formatInvoiceFilters(activeFilters);

      getIbanInvoices({
        ...apiFilters,
        searchText,
        pageNumber: 1,
        pageSize: pageSize,
      });
      rowsPerPageService(RowsPerPageKeys.INVOICES).setRowsPerPage(pageSize);
    },
    [searchText, activeFilters],
  );

  const getIbanInvoices = useCallback(
    (filters: IbanInvoiceGetByFilterQuery) => {
      setIsInvoicesLoading(true);
      dispatch(searchIbanInvoices(filters))
        .unwrap()
        .finally(() => {
          setIsInvoicesLoading(false);
        });
    },
    [dispatch],
  );

  const onOpenCreateModal = useCallback(() => {
    analytics().sendEvent(AnalyticsEvents.invoices_create_invoice);
    navigate(routes.createInvoice, { state: { from: routes.invoices } });
  }, []);

  const handleFilterChange = (
    filterId: keyof InvoiceFiltersState,
    activeFilter: FilterValue,
  ) => {
    setActiveFilters((prevFilters) => ({
      ...prevFilters,
      [filterId]: activeFilter,
    }));
  };

  const onClearFilters = () => {
    setActiveFilters({
      favorite: false,
      ibans: [],
      identificationCodes: [],
      invoiceDateRange: { id: "all_time", range: null },
      expirationDateRange: { id: "all_time", range: null },
    });
  };

  const handleSearchSubmit = useCallback(() => {
    const apiFilters = formatInvoiceFilters(activeFilters);
    getIbanInvoices({ ...apiFilters, searchText, pageNumber: 1, pageSize });
  }, [getIbanInvoices, activeFilters, searchText, pageSize, dispatch]);

  const handleSearchClear = useCallback(() => {
    setSearchText("");
    const apiFilters = formatInvoiceFilters(activeFilters);

    getIbanInvoices({ ...apiFilters, searchText: "", pageNumber: 1, pageSize });
  }, [getIbanInvoices, activeFilters, pageSize, dispatch]);

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        width="100%"
        sx={{ mb: { xs: 2, sm: 3 } }}
      >
        <Box sx={{ mb: { xs: 2, sm: 0 } }}>
          <Typography sx={{ mb: 0.5 }} variant="h3">
            Мої посилання
          </Typography>
          <Typography variant="body1" color="grey700">
            Всі створені посилання
          </Typography>
        </Box>
        <Button
          data-testid="create-invoice-button"
          variant="contained"
          color="primary"
          onClick={onOpenCreateModal}
        >
          Створити посилання
        </Button>
      </Box>
      <SearchInput
        value={searchText}
        onClear={handleSearchClear}
        onChange={setSearchText}
        handleSearchSubmit={handleSearchSubmit}
      />
      <InvoiceFilters
        activeFilters={activeFilters}
        handleFilterChange={handleFilterChange}
        onClearFilters={onClearFilters}
      />
      <InvoicesTable
        loading={isInvoicesLoading || globalLoading}
        ibanInvoices={ibanInvoices}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        pageCount={pageCount}
        pageNumber={pageNumber}
        pageSize={pageSize}
        filters={{ ...activeFilters, searchText }}
      />
    </>
  );
};

export default InvoicesMain;
